/**
 * Data transformation utilities for PAM applications
 */

/**
 * Convert null/undefined values to empty strings in an object
 * Useful for AG Grid which doesn't handle null well in cell editors
 *
 * @param {Object} obj - Object to transform
 * @returns {Object} Object with nulls converted to empty strings
 *
 * @example
 * nullToEmpty({ name: 'John', age: null }) // { name: 'John', age: '' }
 */
export const nullToEmpty = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = value === null || value === undefined ? '' : value;
  }
  return result;
};

/**
 * Convert empty strings to null in an object
 * Useful when preparing data for API submission
 *
 * @param {Object} obj - Object to transform
 * @returns {Object} Object with empty strings converted to null
 *
 * @example
 * emptyToNull({ name: 'John', age: '' }) // { name: 'John', age: null }
 */
export const emptyToNull = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = value === '' ? null : value;
  }
  return result;
};

/**
 * Deep clone an object
 *
 * @param {any} obj - Object to clone
 * @returns {any} Cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;

  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return obj;
  }
};

/**
 * Pick specific keys from an object
 *
 * @param {Object} obj - Source object
 * @param {string[]} keys - Keys to pick
 * @returns {Object} Object with only specified keys
 *
 * @example
 * pick({ a: 1, b: 2, c: 3 }, ['a', 'c']) // { a: 1, c: 3 }
 */
export const pick = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return {};

  const result = {};
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
};

/**
 * Omit specific keys from an object
 *
 * @param {Object} obj - Source object
 * @param {string[]} keys - Keys to omit
 * @returns {Object} Object without specified keys
 *
 * @example
 * omit({ a: 1, b: 2, c: 3 }, ['b']) // { a: 1, c: 3 }
 */
export const omit = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return {};

  const keysSet = new Set(keys);
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!keysSet.has(key)) {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Check if two objects are deeply equal
 *
 * @param {any} obj1 - First object
 * @param {any} obj2 - Second object
 * @returns {boolean} Whether objects are equal
 */
export const isEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (obj1 === null || obj2 === null) return false;
  if (typeof obj1 !== typeof obj2) return false;
  if (typeof obj1 !== 'object') return obj1 === obj2;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!isEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

/**
 * Get changed fields between two objects
 *
 * @param {Object} original - Original object
 * @param {Object} updated - Updated object
 * @returns {Object} Object containing only changed fields with their new values
 *
 * @example
 * getChangedFields({ a: 1, b: 2 }, { a: 1, b: 3 }) // { b: 3 }
 */
export const getChangedFields = (original, updated) => {
  if (!original || !updated) return updated || {};

  const changes = {};
  const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)]);

  for (const key of allKeys) {
    if (!isEqual(original[key], updated[key])) {
      changes[key] = updated[key];
    }
  }

  return changes;
};

/**
 * Group an array of objects by a key
 *
 * @param {Array} array - Array to group
 * @param {string|Function} key - Key to group by (string or function)
 * @returns {Object} Grouped object
 *
 * @example
 * groupBy([{ type: 'a', val: 1 }, { type: 'b', val: 2 }], 'type')
 * // { a: [{ type: 'a', val: 1 }], b: [{ type: 'b', val: 2 }] }
 */
export const groupBy = (array, key) => {
  if (!Array.isArray(array)) return {};

  const getKey = typeof key === 'function' ? key : (item) => item[key];

  return array.reduce((result, item) => {
    const groupKey = getKey(item);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Sort an array of objects by a key
 *
 * @param {Array} array - Array to sort
 * @param {string} key - Key to sort by
 * @param {string} [direction='asc'] - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted array (new array, original unchanged)
 */
export const sortBy = (array, key, direction = 'asc') => {
  if (!Array.isArray(array)) return [];

  const multiplier = direction === 'desc' ? -1 : 1;

  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal === bVal) return 0;
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return multiplier * aVal.localeCompare(bVal);
    }

    return multiplier * (aVal < bVal ? -1 : 1);
  });
};

/**
 * Checks if a value is considered null/empty.
 * Returns true for null, undefined, empty string, or NaN.
 *
 * Note: Returns false for 0 and false — these are valid values.
 *
 * @param {*} value - The value to check
 * @returns {boolean}
 */
export const isValueNull = (value) => {
  if (typeof value === 'number' && isNaN(value)) return true;
  return value === null || value === undefined || value === '';
};
